// material — the per-subdomain client module for a material's generation side
// view (phase 2c; design v2.1 §5): the material's skeleton and its live
// generation job, in the right panel, with the author's Generate / Stop /
// Continue / feedback intents. Implements the module contract
// (modules/module.ts); talks to other modules only through Signals.
//
// Topology (entry file + sibling folder):
//  - material/model.ts      — MaterialModel (Focus sum, Remote, JobFeed)
//  - material/msg.ts        — MaterialMsg (intents, focus facts, outcomes)
//  - material/effect.ts     — MaterialEffect (port calls as data)
//  - material/update.ts     — updateMaterial + inboundMaterial (pure)
//  - material/view-model.ts — rows / controls / status projections (pure)
//  - material/view.tsx      — GenerationSideView (pure React projection)
//  - material/run.ts        — the interpreter (the ONE impure file)

import type { MaterialEffect } from "./material/effect.js";
import { initialMaterialModel, type MaterialModel } from "./material/model.js";
import type { MaterialMsg } from "./material/msg.js";
import { type MaterialDeps, mkRunMaterial } from "./material/run.js";
import {
    inboundMaterial,
    type MaterialRefusal,
    updateMaterial,
} from "./material/update.js";
import type { Module } from "./module.js";

export type {} from "./material/effect.js";
export type { MaterialModel } from "./material/model.js";
export {} from "./material/model.js";
export type { MaterialMsg } from "./material/msg.js";
export type { MaterialDeps } from "./material/run.js";
export type {} from "./material/update.js";
export {} from "./material/update.js";
export { GenerationSideView } from "./material/view.js";
export { isGeneration } from "./material/view-model.js";

export type MaterialModule = Module<
    MaterialModel,
    MaterialMsg,
    MaterialEffect,
    MaterialRefusal
>;

// The module, its interpreter bound to the ports (built once at the mount).
export const mkMaterialModule = (deps: MaterialDeps): MaterialModule => ({
    init: initialMaterialModel,
    update: updateMaterial,
    run: mkRunMaterial(deps),
    inbound: inboundMaterial,
});
